import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { openConsentSettings } from '@/lib/consent';

/**
 * Compact footer rendered on mobile + tablet only (<lg). The full
 * desktop <Footer/> remains hidden below lg, so this gives small-screen
 * users access to legal/contact/info links without competing with the
 * BottomNav. Bottom padding clears the floating BottomNav and the iOS
 * safe-area home indicator (see .pb-bottom-nav utility).
 */
const MobileCompactFooter = () => {
  const { t } = useI18n();
  const location = useLocation();

  // Hide on admin (no public chrome) and on the immersive full-screen
  // map, where a footer would sit on top of the canvas. Legal/contact
  // remain reachable from every other public page.
  if (location.pathname.startsWith('/admin')) return null;
  if (location.pathname === '/map' || location.pathname.startsWith('/map/')) return null;

  const groups = [
    {
      title: t('footer.product'),
      links: [
        { label: t('nav.guide'), to: '/guide' },
        { label: t('nav.map'), to: '/map' },
        { label: t('nav.visibility'), to: '/visibility' },
        { label: t('nav.providers'), to: '/providers' },
      ],
    },
    {
      title: t('footer.platform'),
      links: [
        { label: t('footer.aboutUs'), to: '/about' },
        { label: t('footer.vision'), to: '/vision' },
        { label: t('footer.contactUs'), to: '/contact' },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.imprint'), to: '/impressum' },
        { label: t('footer.privacyPolicy'), to: '/datenschutz' },
        { label: t('footer.termsOfService'), to: '/nutzungsbedingungen' },
      ],
    },
  ];

  return (
    <footer
      aria-label="Site footer"
      className="lg:hidden border-t border-border/40 bg-background/80 backdrop-blur-sm px-5 pt-6 pb-bottom-nav"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-6 gap-x-4">
        {groups.map((g) => (
          <nav key={g.title} aria-label={g.title}>
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
              {g.title}
            </h4>
            <ul className="space-y-1.5">
              {g.links.map((l) => (
                <li key={l.to + l.label}>
                  <Link
                    to={l.to}
                    className="block py-1 text-sm text-foreground/80 hover:text-foreground active:text-foreground transition-colors duration-200"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-border/30 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-[11px] text-muted-foreground/70">
          © {new Date().getFullYear()} scuvio
        </p>
        <button
          type="button"
          onClick={openConsentSettings}
          className="text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors"
        >
          {t('footer.cookieSettings')}
        </button>
      </div>
    </footer>
  );
};

export default MobileCompactFooter;